import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function HowItWorks() {
  return (
    <>
      <Helmet>
        <title>How It Works | Tacoma Off Market Home Buyers</title>
        <meta name="description" content="Learn how our simple 3-step process works to sell your Tacoma house fast for cash. Contact us, get an offer, and close on your timeline." />
      </Helmet>
      
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8 text-green-700 text-center">How It Works</h1>
        <p className="text-lg text-center mb-12 max-w-3xl mx-auto">
          Our process is designed to be simple, transparent, and hassle-free. Here's how we buy your house in three easy steps:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-8 border border-gray-100 rounded-lg shadow-md relative">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold">1</div>
            <h3 className="text-2xl font-bold mb-4 text-green-600 mt-4">Contact Us</h3>
            <p className="mb-4">Fill out our simple form or give us a call to tell us about your property.</p>
            <p className="text-sm text-gray-600">Takes less than 5 minutes</p>
          </div>
          
          <div className="text-center p-8 border border-gray-100 rounded-lg shadow-md relative">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold">2</div>
            <h3 className="text-2xl font-bold mb-4 text-green-600 mt-4">Get Your Offer</h3>
            <p className="mb-4">We'll analyze your property and present you with a fair, no-obligation cash offer within 24 hours.</p>
            <p className="text-sm text-gray-600">No pressure, no obligation</p>
          </div>
          
          <div className="text-center p-8 border border-gray-100 rounded-lg shadow-md relative">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold">3</div>
            <h3 className="text-2xl font-bold mb-4 text-green-600 mt-4">Close On Your Timeline</h3>
            <p className="mb-4">Accept our offer and close on the date that works for you. Get cash in your hands in as little as 7 days.</p>
            <p className="text-sm text-gray-600">We pay all closing costs</p>
          </div>
        </div>
        
        <div className="bg-green-50 p-8 rounded-lg border border-green-200 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-green-700 mb-4 text-center">Common Situations We Help With</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Avoiding foreclosure</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Inheriting an unwanted property</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Relocating quickly for a job</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Divorce or separation</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Tired of being a landlord</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Repairs you can't afford</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Behind on property taxes</span>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
              <span>Fire or water damage</span>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-green-600 text-white font-bold rounded-md hover:bg-green-700 transition duration-300 shadow-md"
            >
              Get Your Cash Offer Now
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default HowItWorks; 